/**
 * The central (M3) stocks with the quantity left once the reserved stocks are
 * subtracted, sorted and quick-searched in the browser, with the central stock
 * integration switch. Admin only.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRedo } from '@fortawesome/free-solid-svg-icons';
import { ROLES } from '@/shared/constants';
import { getUserProfile } from '@/features/auth/session';
import { hardNavigate } from '@/shared/lib/navigation';
import { TopMenu } from '@/shared/layout/TopMenu';
import { SortableHeader } from '@/shared/ui/SortableHeader';
import { useFillerRows } from '@/shared/ui/useFillerRows';
import { useWindowVirtualRows } from '@/shared/ui/useWindowVirtualRows';
import { centralStocksQueryOptions, lastApiCallQueryOptions } from './api';
import {
  filterCentralStocks,
  formatStockDate,
  QUICK_SEARCH_DELAY_MS,
  nextCentralSort,
  sortCentralStocks,
  withCalculatedQuantity,
  type CentralSearch,
  type CentralSortField,
} from './logic';
import { StockToggle } from './StockToggle';
import { kebabCase } from '@/shared/lib/kebab-case';

const COLUMNS: {
  field: CentralSortField;
  label: string;
  search?: keyof CentralSearch;
}[] = [
  { field: 'ean', label: 'EAN', search: 'ean' },
  { field: 'sku', label: 'Product M3SKU (Central)', search: 'sku' },
  { field: 'quantity', label: 'M3 Quantity', search: 'quantity' },
  {
    field: 'calculatedQuantity',
    label: 'Calculated Quantity*',
    search: 'calculatedQuantity',
  },
  { field: 'lastImportId', label: 'Last updated date' },
];

export function CentralStocksPage() {
  const user = getUserProfile();
  const isAdmin = user?.role === ROLES.ADMIN;

  useEffect(() => {
    if (user && !isAdmin) hardNavigate('/main');
  }, [user, isAdmin]);

  const [refresh, setRefresh] = useState(0);
  const [sort, setSort] = useState<{ by: CentralSortField; reverse: boolean }>({
    by: 'ean',
    reverse: false,
  });
  const [search, setSearch] = useState<CentralSearch>({});
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stocks = useQuery({
    ...centralStocksQueryOptions(refresh),
    enabled: isAdmin,
  });
  const lastApiCall = useQuery({
    ...lastApiCallQueryOptions('centralStock', refresh),
    enabled: isAdmin,
  });

  const items = useMemo(
    () =>
      stocks.data
        ? withCalculatedQuantity(stocks.data.stocks, stocks.data.reserved)
        : [],
    [stocks.data],
  );
  const displayed = useMemo(
    () =>
      filterCentralStocks(
        sortCentralStocks(items, sort.by, sort.reverse),
        search,
      ),
    [items, sort, search],
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLTableSectionElement>(null);
  // The central stock can hold thousands of lines: only the rows near the viewport are rendered.
  const virtual = useWindowVirtualRows(
    bodyRef,
    displayed.length,
    'tr.stock-row',
  );
  const fillerRows = useFillerRows(
    containerRef,
    'tr.stock-row, tr.virtual-spacer',
    items.length,
  );

  useEffect(
    () => () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    },
    [],
  );

  if (!isAdmin) return null;

  const quickSearch = (field: keyof CentralSearch, value: string) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(
      () => setSearch((current) => ({ ...current, [field]: value })),
      QUICK_SEARCH_DELAY_MS,
    );
  };

  return (
    <div className="container summary">
      <TopMenu />
      <div className="summary-table mb-12 mt-6">
        <div className="mb-4 inline-flex items-center justify-end">
          <StockToggle configType="centralStocksActive" label="Central Stock" />
          <button
            type="button"
            className="btn btn-dark btn-circle btn-icon"
            aria-label="Refresh"
            disabled={stocks.isFetching}
            onClick={() => setRefresh((count) => count + 1)}
            data-testid="central-stock-refresh-button"
          >
            <FontAwesomeIcon icon={faRedo} spin={stocks.isFetching} />
          </button>
        </div>
        <div className="mb-2 inline-flex justify-end">
          <div
            className="border px-6 py-1"
            data-testid="header-stock-last-update"
          >
            Last updated date:{' '}
            {lastApiCall.data
              ? formatStockDate(lastApiCall.data.lastUpdate)
              : '-'}
          </div>
        </div>
        <div className="mb-4 text-right">
          *Calculated Quantity: without pending / confirmed / ready for
          production (not sent by interface)
        </div>
        <div className="table-container">
          <div
            className="items-container"
            ref={containerRef}
            role="region"
            aria-label="Central stocks"
          >
            <table className="table items">
              <thead>
                <tr>
                  {COLUMNS.map(({ field, label, search: searchField }) => (
                    <SortableHeader
                      key={field}
                      label={label}
                      width="20%"
                      active={sort.by === field}
                      descending={sort.reverse}
                      onClick={() =>
                        setSort((current) => nextCentralSort(current, field))
                      }
                      testId={`central-stock-header-${kebabCase(field)}`}
                    >
                      {searchField && (
                        <>
                          <br />
                          <input
                            className="ml-2 px-2"
                            placeholder="Search..."
                            aria-label={`Search ${label}`}
                            name={searchField}
                            data-testid={`central-stock-search-${kebabCase(searchField)}`}
                            style={{ width: 75 }}
                            onChange={(event) =>
                              quickSearch(searchField, event.target.value)
                            }
                          />
                        </>
                      )}
                    </SortableHeader>
                  ))}
                </tr>
              </thead>
              <tbody ref={bodyRef}>
                {virtual.topSpacer > 0 && (
                  <tr
                    className="virtual-spacer"
                    role="presentation"
                    style={{ height: virtual.topSpacer }}
                  />
                )}
                {displayed.slice(virtual.start, virtual.end).map((item, i) => (
                  <tr key={item._id ?? virtual.start + i} className="stock-row">
                    <td
                      style={{ width: '20%' }}
                      title={item.ean ?? ''}
                      data-testid="central-stock-row-ean"
                    >
                      {item.ean}
                    </td>
                    <td
                      style={{ width: '20%' }}
                      title={item.sku ?? ''}
                      data-testid="central-stock-row-m3sku"
                    >
                      {item.sku}
                    </td>
                    <td
                      style={{ width: '20%' }}
                      title={String(item.quantity ?? '')}
                      data-testid="central-stock-row-m3qty"
                    >
                      {item.quantity}
                    </td>
                    <td
                      style={{ width: '20%' }}
                      title={String(item.calculatedQuantity)}
                      data-testid="central-stock-row-calculated-qty"
                    >
                      {item.calculatedQuantity}
                    </td>
                    <td
                      style={{ width: '20%' }}
                      title={formatStockDate(item.lastImportId)}
                      data-testid="central-stock-row-update-date"
                    >
                      {formatStockDate(item.lastImportId)}
                    </td>
                  </tr>
                ))}
                {virtual.bottomSpacer > 0 && (
                  <tr
                    className="virtual-spacer"
                    role="presentation"
                    style={{ height: virtual.bottomSpacer }}
                  />
                )}
                {Array.from({ length: fillerRows }, (_, index) => (
                  <tr key={`empty-${index}`} className="empty-row">
                    <td style={{ width: '20%' }} />
                    <td style={{ width: '20%' }} />
                    <td style={{ width: '20%' }} />
                    <td style={{ width: '20%' }} />
                    <td style={{ width: '20%' }} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
